import { useState, useEffect, useCallback } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type Platform = 'ios' | 'android' | 'desktop' | 'unknown'
type InstallMethod = 'prompt' | 'manual' | 'unavailable'

interface PWAInstallState {
  platform: Platform
  installMethod: InstallMethod
  canInstall: boolean
  isInstalled: boolean
  isStandalone: boolean
}

/**
 * Hook to handle PWA installation across platforms
 * - Android Chrome: Uses beforeinstallprompt
 * - iOS Safari: Shows manual instructions
 * - Desktop: May use beforeinstallprompt or manual
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [state, setState] = useState<PWAInstallState>(() => ({
    platform: detectPlatform(),
    installMethod: 'unavailable',
    canInstall: false,
    isInstalled: false,
    isStandalone: isStandalone(),
  }))

  // Listen for beforeinstallprompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setState(prev => ({
        ...prev,
        installMethod: 'prompt',
        canInstall: true,
      }))
    }

    window.addEventListener('beforeinstallprompt', handler)
    
    // Check if already installed
    window.addEventListener('appinstalled', () => {
      setState(prev => ({ ...prev, isInstalled: true, canInstall: false }))
      setDeferredPrompt(null)
    })

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
    }
  }, [])

  // Set install method based on platform
  useEffect(() => {
    const platform = detectPlatform()
    let installMethod: InstallMethod = 'unavailable'
    let canInstall = false

    if (state.isStandalone) {
      // Already installed
      installMethod = 'unavailable'
      canInstall = false
    } else if (deferredPrompt) {
      // Chrome/Edge with beforeinstallprompt
      installMethod = 'prompt'
      canInstall = true
    } else if (platform === 'ios' && isSafari()) {
      // iOS Safari - manual instructions
      installMethod = 'manual'
      canInstall = true
    } else if (platform === 'android') {
      // Android without prompt (might be non-Chrome browser)
      installMethod = 'manual'
      canInstall = true
    }

    setState(prev => ({
      ...prev,
      platform,
      installMethod,
      canInstall,
    }))
  }, [deferredPrompt, state.isStandalone])

  // Trigger install prompt (Android Chrome)
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        setState(prev => ({ ...prev, isInstalled: true, canInstall: false }))
        setDeferredPrompt(null)
        return true
      }
      return false
    } catch (error) {
      console.error('Install prompt failed:', error)
      return false
    }
  }, [deferredPrompt])

  return {
    ...state,
    promptInstall,
  }
}

// Helper functions
function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase()
  
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios'
  }
  if (/android/.test(ua)) {
    return 'android'
  }
  if (/windows|macintosh|linux/.test(ua) && !/mobile/.test(ua)) {
    return 'desktop'
  }
  return 'unknown'
}

function isStandalone(): boolean {
  // Check if running as installed PWA
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  )
}

function isSafari(): boolean {
  const ua = navigator.userAgent.toLowerCase()
  return /safari/.test(ua) && !/chrome/.test(ua) && !/crios/.test(ua)
}

